/**
 * The four states every list in this console must have: loading, empty, error, and data.
 * Three of them live here so no screen has to invent them, and so none of them can be
 * skipped by accident (doctrine 9).
 *
 * The writing rules are load-bearing:
 *   - An empty state says a real sentence about why it is empty, in the operator's terms
 *     ("No calls yet tonight. The line is armed and answering."), never "No data".
 *   - An error state says what happened, blames nobody, gives a retry, and — when the
 *     failure is a permission — says who can help instead of just refusing.
 *   - A skeleton is the same shape and height as the thing it becomes, so the page does
 *     not jump when data lands.
 */
import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { ApiError } from '../lib/api.ts';
import { Icon } from './Icon.tsx';
import type { IconName } from './Icon.tsx';

// ------------------------------------------------------------------ skeleton

export function Skeleton({
  width,
  height = 11,
  radius,
  className,
  style,
}: {
  width?: number | string;
  height?: number | string;
  radius?: number;
  className?: string;
  style?: CSSProperties;
}): ReactElement {
  return (
    <span
      className={['nd-skel', className].filter(Boolean).join(' ')}
      aria-hidden="true"
      style={{
        width: width ?? '100%',
        height,
        ...(radius === undefined ? {} : { borderRadius: radius }),
        ...style,
      }}
    />
  );
}

/**
 * Table-shaped placeholder at the real 48px row height. Matching it matters: a skeleton a
 * different size than the content it becomes makes the whole page jump when data lands.
 */
export function SkeletonRows({ rows = 6, columns = 4 }: { rows?: number; columns?: number }): ReactElement {
  const widths = ['38%', '22%', '30%', '18%', '26%', '14%'];
  return (
    <div role="status" aria-live="polite" aria-label="Loading">
      <span className="nd-sr">Loading…</span>
      {Array.from({ length: rows }, (_, r) => (
        <div className="nd-skel-row" key={r}>
          {Array.from({ length: columns }, (_, c) => (
            <Skeleton key={c} width={widths[(r + c) % widths.length]} />
          ))}
        </div>
      ))}
    </div>
  );
}

export function SkeletonCard({ lines = 3 }: { lines?: number }): ReactElement {
  return (
    <div role="status" aria-label="Loading" style={{ padding: 'var(--nd-s4)' }}>
      <span className="nd-sr">Loading…</span>
      <Skeleton width="45%" height={16} />
      <div style={{ height: 12 }} />
      {Array.from({ length: lines }, (_, i) => (
        <div key={i} style={{ marginBottom: 8 }}>
          <Skeleton width={`${90 - i * 12}%`} />
        </div>
      ))}
    </div>
  );
}

/** Four headline numbers, before they arrive. */
export function SkeletonStats({ count = 4 }: { count?: number }): ReactElement {
  return (
    <div className="nd-stats" role="status" aria-label="Loading">
      <span className="nd-sr">Loading…</span>
      {Array.from({ length: count }, (_, i) => (
        <div className="nd-stat" key={i}>
          <Skeleton width="52%" height={12} />
          <div style={{ height: 10 }} />
          <Skeleton width="34%" height={28} />
          <div style={{ height: 10 }} />
          <Skeleton width="66%" height={11} />
        </div>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ empty

export type EmptyStateProps = {
  icon?: IconName;
  title: string;
  /** A real sentence. Say why it is empty and what would change that. */
  body: ReactNode;
  action?: ReactNode;
  compact?: boolean;
};

export function EmptyState({ icon = 'empty-box', title, body, action, compact }: EmptyStateProps): ReactElement {
  return (
    <div className="nd-empty" data-compact={compact ? 'true' : undefined}>
      <span className="nd-empty-icon">
        <Icon name={icon} size={compact ? 17 : 19} strokeWidth={1.3} />
      </span>
      <div className="nd-empty-title">{title}</div>
      <div className="nd-empty-body">{body}</div>
      {action ? <div className="nd-empty-action">{action}</div> : null}
    </div>
  );
}

// ------------------------------------------------------------------ error

export type ErrorStateProps = {
  error: unknown;
  /** What the console was doing, e.g. "load tonight's calls". Used in the sentence. */
  action?: string;
  onRetry?: () => void;
  compact?: boolean;
};

/**
 * Turns any thrown value into a sentence an operator can act on. ApiError already carries
 * the server's own operator-facing detail; anything else gets a generic line plus the raw
 * message in a monospace block, because "something went wrong" with nothing else is the
 * error message people screenshot and send us with the caption "?".
 */
export function ErrorState({ error, action, onRetry, compact }: ErrorStateProps): ReactElement {
  const api = error instanceof ApiError ? error : null;
  const raw = error instanceof Error ? error.message : String(error ?? 'unknown error');

  let title = action ? `Could not ${action}` : 'Something failed';
  let icon: IconName = 'alert-triangle';

  if (api?.isForbidden) {
    title = 'You do not have access to this';
    icon = 'lock';
  } else if (api?.status === 0) {
    title = 'No connection to NightDesk';
    icon = 'wifi-off';
  } else if (api?.isNotFound) {
    title = 'Not found';
    icon = 'empty-box';
  }

  const message = api ? api.message : 'Something on our side failed. Try again in a moment.';
  const showRaw = !api && raw && raw !== message;

  return (
    <div className="nd-errorstate nd-empty" data-compact={compact ? 'true' : undefined} role="alert">
      <span className="nd-empty-icon">
        <Icon name={icon} size={compact ? 17 : 19} strokeWidth={1.3} />
      </span>
      <div className="nd-empty-title">{title}</div>
      <div className="nd-empty-body">{message}</div>
      {showRaw ? (
        <pre className="nd-mono nd-dim" style={{ margin: 0, whiteSpace: 'pre-wrap', maxWidth: '46ch' }}>
          {raw}
        </pre>
      ) : null}
      {onRetry && (!api || api.retryable || api.isNotFound) ? (
        <div className="nd-empty-action">
          <button type="button" className="nd-btn" onClick={onRetry}>
            <Icon name="refresh" size={13} />
            Try again
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The whole loading/empty/error decision in one place, for the many panels that are just
 * "fetch a thing, then render it".
 */
export function AsyncPanel<T>({
  query,
  action,
  empty,
  isEmpty,
  skeleton,
  children,
}: {
  query: { data: T | undefined; isLoading: boolean; isError: boolean; error: unknown; refetch: () => unknown };
  action?: string;
  empty?: EmptyStateProps;
  isEmpty?: (data: T) => boolean;
  skeleton?: ReactNode;
  children: (data: T) => ReactNode;
}): ReactElement {
  if (query.isLoading) return <>{skeleton ?? <SkeletonRows />}</>;
  if (query.isError || query.data === undefined) {
    return <ErrorState error={query.error} action={action} onRetry={() => void query.refetch()} />;
  }
  if (empty && isEmpty?.(query.data)) return <EmptyState {...empty} />;
  return <>{children(query.data)}</>;
}
